import * as THREE from 'three';
import { createEnemyVisualKit } from '../assets/EnemyModelKit';
import { PLAYFIELD, SIMULATION } from '../game/constants';
import type { BulletSystem } from './BulletSystem';
import type { PlayerShotSystem } from './PlayerShotSystem';
import { emit, type PatternSpec } from './PatternGrammar';
import { stepFlock, stepKamikaze, stepLance, relayMultiplier } from './SwarmBehavior';
import { ARMOR_RESPONSE, ArmorClass, type WeaponId } from '../game/combat';
import { getDifficulty } from '../game/difficulty';



/**
 * The full Stage 1 roster. Bible 05-BESTIARY §5 roster contract:
 * DRN-01..07, AIR-01, GND-01, STA-01 — all ten belong in HOME FIELD.
 *
 * Each teaches a different dodging skill, which is the whole reason there are
 * ten rather than one with scaled health.
 */
export const EnemyRole = {
  /** DRN-01 TICK — Flock. Crowd flow. */
  Tick: 0,
  /** DRN-02 PICKET — Kamikaze. Threat triage. */
  Picket: 1,
  /** DRN-03 ROUNDEL — Lance. Lateral commitment. Also acts as a Relay. */
  Roundel: 2,
  /** DRN-04 SPLINTER — fast FPV kamikaze. Shorter fuse than PICKET. */
  Splinter: 3,
  /** DRN-05 DRAYMAN — Carrier. Births TICKs. Kill the source or drown. */
  Drayman: 4,
  /** DRN-06 SHRIKE — Curtain. Rigid lattice with an authored gap. */
  Shrike: 5,
  /** DRN-07 CROSSTALK — Relay. Non-firing; buffs everything in radius. */
  Crosstalk: 6,
  /** AIR-01 KESTREL — manned interceptor. Arc pass, aimed bolts. */
  Kestrel: 7,
  /** GND-01 GUARD — tracked tank on the terrain plane. Cannot ram you. */
  Guard: 8,
  /** STA-01 GUN POD — static emplacement. Five-fan with fixed gaps. */
  GunPod: 9,
} as const;

/**
 * Firing patterns per role, written in the closed grammar (PatternGrammar.ts).
 *
 * These are the shapes that make the screen read as bullet ART rather than as
 * scattered fire: every one is authored hole-first, so the player is looking at
 * a corridor rather than counting projectiles. `gapIndex` is the composed
 * omission — the invitation.
 */
const ROLE_PATTERNS: Record<number, PatternSpec[]> = {
  // TICK — the flock. Individually trivial, collectively a moving texture.
  // A 5-wide aimed fan from forty drones is the signature Stage 1 image.
  [EnemyRole.Tick]: [
    { kind: 'fan', count: 4, speed: 11, spreadDeg: 32, aimed: true, cadenceHz: 0.42, telegraphMs: 400, bullet: 'pellet' },
  ],
  // PICKET — dives rather than shoots, but seeds a ring on approach so the
  // dive has a consequence even if the player kills it early.
  [EnemyRole.Picket]: [
    { kind: 'ring', count: 12, speed: 9, gapIndex: 3, gapWidth: 2, cadenceHz: 0.3, telegraphMs: 450, bullet: 'pellet' },
  ],
  // SPLINTER — no projectile; the body is the threat.
  [EnemyRole.Splinter]: [],
  // DRAYMAN — the carrier. Sparse aimed pairs; its real threat is the births.
  [EnemyRole.Drayman]: [
    { kind: 'aimed_burst', count: 2, speed: 14, spreadDeg: 12, aimed: true, cadenceHz: 0.55, telegraphMs: 600, bullet: 'pellet' },
  ],
  // SHRIKE — Curtain. Each member fires ONE static pellet; the wall is made of
  // the formation, not of any one unit. The authored gap is the answer.
  [EnemyRole.Shrike]: [
    { kind: 'fan', count: 1, speed: 11, spreadDeg: 0, cadenceHz: 0.55, telegraphMs: 500, bullet: 'pellet' },
  ],
  // CROSSTALK — non-firing by design. Its threat is entirely informational.
  [EnemyRole.Crosstalk]: [],
  // KESTREL — manned interceptor. Fast aimed bolt pairs on an arc pass.
  [EnemyRole.Kestrel]: [
    { kind: 'aimed_burst', count: 2, speed: 22, spreadDeg: 6, aimed: true, cadenceHz: 1.1, telegraphMs: 400, bullet: 'bolt' },
  ],
  // GUARD — tracked tank. Aimed triple after a 350 ms barrel charge.
  [EnemyRole.Guard]: [
    { kind: 'aimed_burst', count: 3, speed: 16, spreadDeg: 14, aimed: true, cadenceHz: 0.62, telegraphMs: 350, bullet: 'pellet' },
  ],
  // GUN POD — static five-fan with fixed 2.2 u gaps. Tells you where to stand.
  [EnemyRole.GunPod]: [
    { kind: 'fan', count: 5, speed: 10, spreadDeg: 52, gapIndex: 2, gapWidth: 1, cadenceHz: 0.5, telegraphMs: 500, bullet: 'pellet' },
  ],
  // ROUNDEL — the heavy. Alternates a wide static petal (tells you where to
  // stand) with an aimed burst (forces you to leave). That alternation IS the
  // grammar of the genre.
  [EnemyRole.Roundel]: [
    { kind: 'petal', count: 8, speed: 10, spreadDeg: 15, gapIndex: 2, gapWidth: 1, cadenceHz: 0.34, telegraphMs: 500, bullet: 'pellet' },
    { kind: 'aimed_burst', count: 3, speed: 18, spreadDeg: 10, aimed: true, cadenceHz: 0.34, telegraphMs: 400, bullet: 'bolt' },
  ],
};

export type EnemyRoleId = (typeof EnemyRole)[keyof typeof EnemyRole];

/**
 * Armor class per role (bible 05-BESTIARY). This is what makes the drifting
 * red/blue pod a tactical decision rather than a cosmetic one:
 *
 *   HAILSTORM (red spread)  1.00 vs LIGHT   0.75 vs ARMORED
 *   ARCLIGHT  (blue lance)  0.85 vs LIGHT   1.00 vs ARMORED
 *
 * So a TICK swarm melts under red and a ROUNDEL shrugs it off. Carrying the
 * wrong pod into the wrong wave is a real, felt penalty.
 */
const ROLE_ARMOR: Record<number, ArmorClass> = {
  [EnemyRole.Tick]: ArmorClass.LIGHT,
  [EnemyRole.Picket]: ArmorClass.LIGHT,
  [EnemyRole.Roundel]: ArmorClass.ARMORED,
  [EnemyRole.Splinter]: ArmorClass.LIGHT,
  [EnemyRole.Drayman]: ArmorClass.ARMORED,
  [EnemyRole.Shrike]: ArmorClass.LIGHT,
  [EnemyRole.Crosstalk]: ArmorClass.LIGHT,
  [EnemyRole.Kestrel]: ArmorClass.LIGHT,
  [EnemyRole.Guard]: ArmorClass.ARMORED,
  [EnemyRole.GunPod]: ArmorClass.ARMORED,
};

/** HP per role, quoted from bible 05-BESTIARY. */
const ROLE_HP: Record<number, number> = {
  [EnemyRole.Tick]: 8,
  [EnemyRole.Picket]: 22,
  [EnemyRole.Roundel]: 70,
  [EnemyRole.Splinter]: 16,
  [EnemyRole.Drayman]: 1100,
  [EnemyRole.Shrike]: 48,
  [EnemyRole.Crosstalk]: 120,
  [EnemyRole.Kestrel]: 90,
  [EnemyRole.Guard]: 320,
  [EnemyRole.GunPod]: 140,
};

/** Score per role. */
const ROLE_SCORE: Record<number, number> = {
  [EnemyRole.Tick]: 80,
  [EnemyRole.Picket]: 180,
  [EnemyRole.Roundel]: 500,
  [EnemyRole.Splinter]: 140,
  [EnemyRole.Drayman]: 4000,
  [EnemyRole.Shrike]: 310,
  [EnemyRole.Crosstalk]: 650,
  [EnemyRole.Kestrel]: 500,
  [EnemyRole.Guard]: 1200,
  [EnemyRole.GunPod]: 450,
};

/** Collision radius per role. */
const ROLE_RADIUS: Record<number, number> = {
  [EnemyRole.Tick]: 0.62,
  [EnemyRole.Picket]: 0.62,
  [EnemyRole.Roundel]: 0.88,
  [EnemyRole.Splinter]: 0.5,
  [EnemyRole.Drayman]: 1.9,
  [EnemyRole.Shrike]: 0.72,
  [EnemyRole.Crosstalk]: 0.8,
  [EnemyRole.Kestrel]: 1.0,
  [EnemyRole.Guard]: 1.5,
  [EnemyRole.GunPod]: 1.2,
};

/**
 * GND, NAV and STA classes sit ON the terrain plane. The player flies over
 * them, so they can never have a lethal contact volume (bible 05 §0.3).
 */
const TERRAIN_LOCKED = new Set<number>([EnemyRole.Guard, EnemyRole.GunPod]);

/** Render scale per role. Size is the first thing the eye separates. */
const ROLE_VISUAL_SCALE: Record<number, number> = {
  [EnemyRole.Tick]: 0.88,
  [EnemyRole.Picket]: 0.94,
  [EnemyRole.Roundel]: 0.92,
  [EnemyRole.Splinter]: 0.7,
  [EnemyRole.Drayman]: 1.5,
  [EnemyRole.Shrike]: 0.95,
  [EnemyRole.Crosstalk]: 1.05,
  [EnemyRole.Kestrel]: 1.15,
  [EnemyRole.Guard]: 1.3,
  [EnemyRole.GunPod]: 1.1,
};

/**
 * Draw height per role. GND and STA classes sit ON the terrain plane — the
 * player flies over them — so they must render visibly lower than aircraft.
 */
const ROLE_ALTITUDE: Record<number, number> = {
  [EnemyRole.Tick]: 1.04,
  [EnemyRole.Picket]: 1.12,
  [EnemyRole.Roundel]: 1.18,
  [EnemyRole.Splinter]: 0.98,
  [EnemyRole.Drayman]: 1.32,
  [EnemyRole.Shrike]: 1.1,
  [EnemyRole.Crosstalk]: 1.22,
  [EnemyRole.Kestrel]: 1.26,
  [EnemyRole.Guard]: -1.6,
  [EnemyRole.GunPod]: -1.7,
};

/** Base damage per player shot before the armor multiplier. */
const BASE_SHOT_DAMAGE = 7;

/**
 * Crossfire damage. Now ABOVE a player shot (9 vs 7), because arranging a
 * crossfire is harder than pointing the gun — it requires reading the field
 * and placing yourself. Rewarding the harder action more is correct, and it
 * gives dense waves a genuine counter-play instead of just a health check.
 */
const CROSSFIRE_DAMAGE = 9;

export class EnemySystem {
  readonly group = new THREE.Group();
  private readonly active = new Uint8Array(SIMULATION.maxEnemies);
  private readonly role = new Uint8Array(SIMULATION.maxEnemies);
  private readonly x = new Float32Array(SIMULATION.maxEnemies);
  private readonly y = new Float32Array(SIMULATION.maxEnemies);
  private readonly baseX = new Float32Array(SIMULATION.maxEnemies);
  private readonly vx = new Float32Array(SIMULATION.maxEnemies);
  private readonly vy = new Float32Array(SIMULATION.maxEnemies);
  private readonly hp = new Float32Array(SIMULATION.maxEnemies);
  private readonly age = new Float32Array(SIMULATION.maxEnemies);
  private readonly fireCooldown = new Float32Array(SIMULATION.maxEnemies);
  /** Formation slot — Lance file order, Curtain lattice index. */
  private readonly slot = new Uint8Array(SIMULATION.maxEnemies);
  /** Kamikaze lock state. 0 = still hovering, 1 = committed. */
  private readonly locked = new Uint8Array(SIMULATION.maxEnemies);
  /** Reused spatial grid for flock neighbour lookup — allocated once. */
  private readonly flockGrid = new Map<number, number[]>();
  /** Rolling counter so each spawn of a role gets a distinct formation slot. */
  private slotCounter = 0;
  /** Carrier birth cadence. */
  private readonly birthTimer = new Float32Array(SIMULATION.maxEnemies);
  private readonly visualKit = createEnemyVisualKit(SIMULATION.maxEnemies);
  private readonly dummy = new THREE.Object3D();
  count = 0;
  /** Fired when a Kamikaze commits, so the game can play the lock tell. */
  onLock: ((x: number, y: number) => void) | null = null;

  constructor() {
    this.group.name = 'InheritedDroneFormation';
    this.group.add(this.visualKit.group);
  }

  spawn(role: EnemyRoleId, x: number, y: number, targetX = 0, targetY = -16): void {
    const index = this.findFree();
    if (index < 0) return;
    this.active[index] = 1;
    this.role[index] = role;
    this.x[index] = x;
    this.y[index] = y;
    this.baseX[index] = x;
    this.age[index] = 0;
    this.locked[index] = 0;
    this.slot[index] = this.slotCounter++ % 12;
    this.birthTimer[index] = 1.6;
    this.fireCooldown[index] = 0.75 + (index % 5) * 0.16;
    this.hp[index] = ROLE_HP[role] ?? 20;

    if (role === EnemyRole.Tick) {
      this.vx[index] = 0;
      this.vy[index] = -5.4;
    } else if (role === EnemyRole.Picket || role === EnemyRole.Splinter) {
      const dx = targetX - x;
      const dy = targetY - y;
      const length = Math.max(0.001, Math.sqrt(dx * dx + dy * dy));
      const speed = role === EnemyRole.Splinter ? 13 : 11;
      this.vx[index] = (dx / length) * speed;
      this.vy[index] = (dy / length) * speed;
    } else if (role === EnemyRole.Kestrel) {
      // Arc pass: enters angled and never returns.
      this.vx[index] = x > 0 ? -7.5 : 7.5;
      this.vy[index] = -9.0;
    } else if (role === EnemyRole.Drayman) {
      this.vx[index] = 0;
      this.vy[index] = -2.8;
    } else if (TERRAIN_LOCKED.has(role)) {
      // Ground units do not manoeuvre — they ride the scroll.
      this.vx[index] = 0;
      this.vy[index] = -12;
    } else {
      this.vx[index] = x > 0 ? -1.4 : 1.4;
      this.vy[index] = -4.1;
    }
    this.count += 1;
  }

  update(
    delta: number,
    playerX: number,
    playerY: number,
    bullets: BulletSystem,
  ): void {
    // TICK flocks as one body — boids over a uniform grid, O(n) not O(n^2).
    stepFlock(
      this.active.length,
      this.active,
      this.role,
      EnemyRole.Tick,
      this.x,
      this.y,
      this.vx,
      this.vy,
      delta,
      playerX,
      this.flockGrid,
    );

    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      this.age[index] += delta;
      const role = this.role[index];

      // ROUNDEL acts as a Relay: anything inside its radius fires faster.
      // Killing the heavy first is therefore correct, not merely optimal.
      const cadence = relayMultiplier(
        index,
        this.active.length,
        this.active,
        this.role,
        EnemyRole.Roundel,
        this.x,
        this.y,
      );
      this.fireCooldown[index] -= delta * cadence;

      if (role === EnemyRole.Tick) {
        // Movement already applied by stepFlock above.
        if (this.fireCooldown[index] <= 0 && this.y[index] < 21) {
          this.firePattern(index, role, bullets, playerX, playerY);
          this.fireCooldown[index] = 1.9 * getDifficulty().cadenceScale;
        }
      } else if (role === EnemyRole.Crosstalk) {
        // Relay: drifts slowly and never fires. Its whole threat is the buff
        // it grants everything around it — which is why it dies first.
        this.x[index] = this.baseX[index] + Math.sin(this.age[index] * 0.9) * 2.2;
        this.y[index] -= 3.0 * delta;
      } else if (role === EnemyRole.Shrike) {
        // Curtain: rigid lattice, gentle drift, fires between members.
        this.x[index] = this.baseX[index] + Math.sin(this.age[index] * 0.7) * 1.8;
        this.y[index] -= 2.4 * delta;
        if (this.fireCooldown[index] <= 0 && this.y[index] < 21) {
          this.firePattern(index, role, bullets, playerX, playerY);
          this.fireCooldown[index] = 1.8 * getDifficulty().cadenceScale;
        }
      } else if (role === EnemyRole.Drayman) {
        // Carrier: slow, near-straight, births TICKs on a cadence.
        this.x[index] += this.vx[index] * delta;
        this.y[index] += this.vy[index] * delta;
        this.birthTimer[index] -= delta;
        if (this.birthTimer[index] <= 0 && this.y[index] < 20 && this.y[index] > -6) {
          for (let k = 0; k < 4; k += 1) {
            this.spawn(EnemyRole.Tick, this.x[index] + (k - 1.5) * 1.5, this.y[index] - 1.5);
          }
          this.birthTimer[index] = 2.4;
        }
        if (this.fireCooldown[index] <= 0 && this.y[index] < 21) {
          this.firePattern(index, role, bullets, playerX, playerY);
          this.fireCooldown[index] = 1.8 * getDifficulty().cadenceScale;
        }
      } else if (TERRAIN_LOCKED.has(role)) {
        // Terrain-locked: pure scroll. Turrets rotate; the hull does not move.
        this.y[index] += this.vy[index] * delta;
        if (this.fireCooldown[index] <= 0 && this.y[index] < 20 && this.y[index] > -18) {
          this.firePattern(index, role, bullets, playerX, playerY);
          this.fireCooldown[index] = (role === EnemyRole.Guard ? 1.6 : 2.0) * getDifficulty().cadenceScale;
        }
      } else if (role === EnemyRole.Kestrel) {
        // Arc pass — a single curved sweep, firing as it crosses.
        this.vx[index] *= 1 - 0.35 * delta;
        this.x[index] += this.vx[index] * delta;
        this.y[index] += this.vy[index] * delta;
        if (this.fireCooldown[index] <= 0 && this.y[index] < 21) {
          this.firePattern(index, role, bullets, playerX, playerY);
          this.fireCooldown[index] = 0.9 * getDifficulty().cadenceScale;
        }
      } else if (role === EnemyRole.Picket || role === EnemyRole.Splinter) {
        // Kamikaze: hover, lock at the END of the delay, then commit.
        const justLocked = stepKamikaze(
          index,
          this.x,
          this.y,
          this.vx,
          this.vy,
          this.age,
          this.locked,
          delta,
          playerX,
          playerY,
        );
        if (justLocked) this.onLock?.(this.x[index], this.y[index]);
      } else {
        // ROUNDEL flies a weaving column — Lance.
        stepLance(index, this.slot[index], this.x, this.y, this.baseX, this.age, delta);
        if (this.fireCooldown[index] <= 0 && this.y[index] < 20) {
          this.firePattern(index, role, bullets, playerX, playerY);
          this.fireCooldown[index] = 1.6 * getDifficulty().cadenceScale;
        }
      }
      if (
        this.y[index] < PLAYFIELD.despawnY ||
        Math.abs(this.x[index]) > PLAYFIELD.halfWidth + 6
      ) {
        this.deactivate(index);
      }
    }
  }

  /**
   * The armour class most represented on screen right now. Drives the HUD's
   * weapon-effectiveness bars so the readout reflects the live threat rather
   * than a static table.
   */
  /**
   * Area damage — the bomb. Previously the bomb cleared bullets and levelled
   * props but did nothing to enemies, which is why it read as spectacle
   * without payoff. Returns the kills and score so the caller can bank them.
   */
  damageArea(
    wx: number,
    wy: number,
    radius: number,
    amount: number,
    onKill?: (x: number, y: number) => void,
  ): { kills: number; score: number } {
    let kills = 0;
    let score = 0;
    const r2 = radius * radius;
    for (let i = 0; i < this.active.length; i += 1) {
      if (!this.active[i]) continue;
      const dx = this.x[i] - wx;
      const dy = this.y[i] - wy;
      if (dx * dx + dy * dy > r2) continue;
      this.hp[i] -= amount;
      if (this.hp[i] <= 0) {
        kills += 1;
        score += ROLE_SCORE[this.role[i]] ?? 100;
        onKill?.(this.x[i], this.y[i]);
        this.deactivate(i);
      }
    }
    return { kills, score };
  }

  /**
   * Resolve enemy-on-enemy crossfire. Returns kills and score so the caller
   * can bank them — crossfire kills are worth MORE than normal kills, because
   * arranging one is a deliberate act of play rather than pointing the gun.
   */
  resolveCrossfire(
    bullets: BulletSystem,
    onHit: (x: number, y: number, killed: boolean) => void,
  ): { kills: number; score: number } {
    let kills = 0;
    let score = 0;
    bullets.resolveCrossfire(
      this.active.length,
      this.active,
      this.x,
      this.y,
      this.hp,
      (i) => ROLE_RADIUS[this.role[i]] ?? 0.62,
      CROSSFIRE_DAMAGE,
      (x, y, index, killed) => {
        onHit(x, y, killed);
        if (killed) {
          kills += 1;
          // 1.5x bounty: the player set this up, and it should feel rewarded.
          score += Math.round((ROLE_SCORE[this.role[index]] ?? 100) * 1.5);
          this.deactivate(index);
        }
      },
    );
    return { kills, score };
  }

  /** Accessors for external systems that need to read the entity store. */
  get activeCount(): number { return this.active.length; }
  get activeFlags(): Uint8Array { return this.active; }
  get posX(): Float32Array { return this.x; }
  get posY(): Float32Array { return this.y; }

  /** Apply damage to one enemy by index. Used by homing missiles. */
  damageIndex(index: number, amount: number): { killed: boolean; score: number } {
    if (!this.active[index]) return { killed: false, score: 0 };
    this.hp[index] -= amount;
    if (this.hp[index] > 0) return { killed: false, score: 0 };
    const score = ROLE_SCORE[this.role[index]] ?? 100;
    this.deactivate(index);
    return { killed: true, score };
  }

  dominantArmor(): ArmorClass | null {
    const tally: Record<number, number> = {};
    let best: ArmorClass | null = null;
    let bestN = 0;
    for (let i = 0; i < this.active.length; i += 1) {
      if (!this.active[i]) continue;
      const a = ROLE_ARMOR[this.role[i]] ?? ArmorClass.LIGHT;
      tally[a] = (tally[a] ?? 0) + 1;
      if (tally[a] > bestN) {
        bestN = tally[a];
        best = a;
      }
    }
    return best;
  }

  resolvePlayerShots(
    shots: PlayerShotSystem,
    onImpact?: (x: number, y: number, destroyed: boolean, role: EnemyRoleId, effectiveness?: number) => void,
    weapon: WeaponId = 'HAILSTORM',
  ): { kills: number; score: number } {
    let kills = 0;
    let score = 0;
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      const radius = ROLE_RADIUS[this.role[index]] ?? 0.62;
      if (!shots.collideCircle(this.x[index], this.y[index], radius)) continue;
      // Armor matchup. The multiplier is also handed to the impact callback so
      // the spark VFX can show the player whether their weapon is working.
      const armor = ROLE_ARMOR[this.role[index]] ?? ArmorClass.LIGHT;
      const mult = ARMOR_RESPONSE[weapon][armor];
      this.hp[index] -= BASE_SHOT_DAMAGE * mult;
      const destroyed = this.hp[index] <= 0;
      // Normalise 0.55..1.4 into 0..1 for the spark's hot/dull read.
      const effectiveness = Math.max(0, Math.min(1, (mult - 0.55) / 0.85));
      onImpact?.(this.x[index], this.y[index], destroyed, this.role[index] as EnemyRoleId, effectiveness);
      if (destroyed) {
        kills += 1;
        score += ROLE_SCORE[this.role[index]] ?? 100;
        this.deactivate(index);
      }
    }
    return { kills, score };
  }

  collidePlayer(px: number, py: number, radius: number): boolean {
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      const dx = this.x[index] - px;
      const dy = this.y[index] - py;
      const enemyRadius = this.role[index] === EnemyRole.Roundel ? 0.72 : 0.48;
      const total = radius + enemyRadius;
      if (dx * dx + dy * dy <= total * total) {
        this.deactivate(index);
        return true;
      }
    }
    return false;
  }

  syncVisuals(): void {
    // One counter per role. Sizing this to the roster is mandatory: a short
    // array silently drops every role past its length.
    const counts = new Array<number>(this.visualKit.roles.length).fill(0);
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      const role = this.role[index];
      const meshIndex = counts[role];
      const cycle = (this.age[index] * 2.4 + index * 0.173) % 1;
      const pulse = 1 + Math.sin(cycle * Math.PI * 2) * 0.025;
      const baseRotation = role === EnemyRole.Picket
        ? Math.atan2(-this.vx[index], this.vy[index])
        : role === EnemyRole.Tick
          ? Math.sin(this.age[index] * 1.8 + index) * 0.14
          : Math.sin(this.age[index] * 0.9 + index * 0.3) * 0.07;
      // Scale and altitude per role, so silhouettes separate by size and the
      // layer they occupy — ground units sit low, aircraft ride high.
      const roleScale = ROLE_VISUAL_SCALE[role] ?? 0.92;
      const altitude = ROLE_ALTITUDE[role] ?? 1.18;

      this.visualKit.roles[role].forEach((part) => {
        const motionScale = part.motion === 'growth'
          ? 1 + Math.sin(this.age[index] * 5.5 + index) * 0.07
          : pulse;
        const rotorTurn = part.motion === 'rotor'
          ? this.age[index] * (index % 2 ? -18 : 18)
          : 0;
        this.dummy.position.set(this.x[index], this.y[index], altitude);
        this.dummy.rotation.set(0, 0, baseRotation + rotorTurn);
        this.dummy.scale.setScalar(roleScale * motionScale);
        this.dummy.updateMatrix();
        part.mesh.setMatrixAt(meshIndex, this.dummy.matrix);
      });
      counts[role] += 1;
    }
    this.visualKit.roles.forEach((parts, role) => {
      parts.forEach((part) => {
        part.mesh.count = counts[role];
        part.mesh.instanceMatrix.needsUpdate = true;
      });
    });
  }

  reset(): void {
    this.active.fill(0);
    this.count = 0;
    this.syncVisuals();
  }

  dispose(): void {
    this.visualKit.dispose();
  }

  /**
   * Fire one volley of this role's authored pattern.
   *
   * `phase` advances per shot so spirals and petals rotate between volleys —
   * this is what turns a repeated shape into a motif with variation rather
   * than a metronome.
   */
  private firePattern(
    index: number,
    role: number,
    bullets: BulletSystem,
    playerX: number,
    playerY: number,
  ): void {
    const patterns = ROLE_PATTERNS[role];
    if (!patterns || patterns.length === 0) return;
    // Alternate between a role's patterns so heavies do not repeat themselves.
    const base = patterns[Math.floor(this.age[index] * 0.5 + index) % patterns.length];
    const diff = getDifficulty();
    // Scale count and speed only. The SHAPE of every pattern — and therefore
    // the gap the player is meant to read — is identical on every tier.
    const spec = {
      ...base,
      count: Math.max(1, Math.round(base.count * diff.bulletScale)),
      speed: base.speed * diff.speedScale,
    };
    const phase = this.age[index] * 0.9 + index * 0.37;
    const shots = emit(spec, this.x[index], this.y[index], playerX, playerY, phase);
    for (const b of shots) {
      bullets.spawn(b.x, b.y, b.vx, b.vy, b.radius * 0.55, 8, index);
    }
  }

  private findFree(): number {
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) return index;
    }
    return -1;
  }

  private deactivate(index: number): void {
    this.active[index] = 0;
    this.count -= 1;
  }
}
